import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSite } from '../context/SiteContext';
import '../assets/LegalPage.css';

const TermsOfUse = () => {
  const { siteName, fullAddress, email, phone } = useSite();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const effectiveDate = 'March 19, 2025';
  const lastUpdated = 'March 19, 2025';

  return (
    <div className="legal-page">
      <div className="legal-container">
        <header className="legal-header">
          <h1>Terms of Use</h1>
          <p className="legal-meta">
            Effective Date: {effectiveDate} | Last Updated: {lastUpdated}
          </p>
        </header>

        <div className="legal-content">
          <section className="legal-section">
            <h2>1. Agreement to Terms</h2>
            <p>
              Welcome to {siteName}. By accessing or using our website, mobile application, or any related services (collectively, the "Services"), you agree to be bound by these Terms of Use ("Terms"). If you do not agree to these Terms, do not use our Services. We reserve the right to modify these Terms at any time; your continued use constitutes acceptance of any changes.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Eligibility</h2>
            <p>
              You must be at least 18 years of age and have the legal capacity to enter into a binding contract to use our Services. By using our Services, you represent and warrant that you meet these requirements. If you are under 18, you may use our Services only with the involvement of a parent or guardian. Our Services are intended for use only within the United States.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. Account Registration and Security</h2>
            <p>
              You may be required to create an account to access certain features. You agree to provide accurate, current, and complete information and to update it as needed. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account. Notify us immediately of any unauthorized use.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Products, Pricing, and Availability</h2>
            <p>
              We strive to display our products and prices accurately. However, we do not warrant that product descriptions, pricing, or other content is accurate, complete, or error-free. We reserve the right to correct errors, inaccuracies, or omissions and to change or update information at any time without prior notice. Product availability is subject to change. We reserve the right to limit quantities and to discontinue products at any time.
            </p>
            <p>
              All prices are in U.S. dollars and exclude applicable taxes unless otherwise stated. Sales tax will be calculated at checkout based on your shipping address and applicable state and local laws.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Orders and Payment</h2>
            <p>
              When you place an order, you are making an offer to purchase. We reserve the right to accept or decline your order for any reason, including product availability, errors in pricing or product information, or suspected fraud. Order confirmation does not constitute acceptance; acceptance occurs when we ship the product.
            </p>
            <p>
              We accept major credit cards, debit cards, PayPal, and other payment methods as displayed at checkout. You represent that you are authorized to use the chosen payment method. We may use third-party payment processors; their terms apply to payment processing.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Shipping and Delivery</h2>
            <p>
              Shipping times and costs vary by method and destination. Estimated delivery dates are not guaranteed. Risk of loss and title pass to you upon delivery to the carrier. We are not responsible for delays caused by carriers or customs. For detailed shipping information, please see our <Link to="/shipping">Shipping Policy</Link>.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Returns and Refunds</h2>
            <p>
              Our return and refund policy is set forth in our <Link to="/returns">Returns Policy</Link>. By making a purchase, you agree to the terms of that policy. We reserve the right to refuse returns that do not comply with our policy.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Prohibited Conduct</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use our Services for any unlawful purpose or in violation of any applicable laws</li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>Interfere with or disrupt our Services or servers</li>
              <li>Attempt to gain unauthorized access to our systems, accounts, or networks</li>
              <li>Use automated means (bots, scrapers) to access our Services without permission</li>
              <li>Transmit viruses, malware, or other harmful code</li>
              <li>Collect or harvest user information without consent</li>
              <li>Use our Services to send spam or unsolicited communications</li>
            </ul>
            <p>We reserve the right to suspend or terminate your access for any violation of these Terms.</p>
          </section>

          <section className="legal-section">
            <h2>9. Intellectual Property</h2>
            <p>
              All content on our Services—including text, graphics, logos, images, and software—is the property of {siteName} or its licensors and is protected by U.S. and international copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works without our express written permission.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. User Content</h2>
            <p>
              If you submit reviews, comments, photos, or other content ("User Content"), you grant us a non-exclusive, royalty-free, perpetual license to use, reproduce, and display such content in connection with our Services. You represent that you own or have the right to submit User Content and that it does not violate any third-party rights or applicable laws. We may remove User Content at our discretion.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Disclaimers</h2>
            <p>
              OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT OUR SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Limitation of Liability</h2>
            <p>
              TO THE FULLEST EXTENT PERMITTED BY LAW, {siteName} AND ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF OUR SERVICES. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE PRODUCTS OR SERVICES GIVING RISE TO THE CLAIM, OR $100, WHICHEVER IS GREATER.
            </p>
            <p>
              Some states do not allow the exclusion or limitation of certain damages; in such states, our liability is limited to the maximum extent permitted by law.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless {siteName} and its affiliates, officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorneys' fees) arising from your use of our Services, your violation of these Terms, or your violation of any third-party rights.
            </p>
          </section>

          <section className="legal-section">
            <h2>14. Dispute Resolution and Arbitration</h2>
            <p>
              Any dispute arising from these Terms or your use of our Services shall be resolved by binding arbitration in accordance with the rules of the American Arbitration Association, except that either party may seek injunctive relief in court. You waive any right to participate in a class action. The arbitration shall be conducted in the state where our principal place of business is located. The arbitrator's decision shall be final and binding.
            </p>
          </section>

          <section className="legal-section">
            <h2>15. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State of New York, without regard to its conflict of law principles. Any legal action not subject to arbitration shall be brought in the state or federal courts located in New York County, New York.
            </p>
          </section>

          <section className="legal-section">
            <h2>16. General Provisions</h2>
            <p>
              If any provision of these Terms is found to be unenforceable, the remaining provisions shall remain in effect. Our failure to enforce any right shall not constitute a waiver. These Terms constitute the entire agreement between you and {siteName} regarding the Services. We may assign our rights and obligations under these Terms; you may not assign without our consent.
            </p>
          </section>

          <section className="legal-section">
            <h2>17. Contact Us</h2>
            <p>
              For questions about these Terms of Use, please contact us:
            </p>
            <ul className="legal-contact">
              <li><strong>{siteName}</strong></li>
              <li>{fullAddress}</li>
              <li>Email: <a href={`mailto:${email}`}>{email}</a></li>
              <li>Phone: <a href={`tel:${phone}`}>{phone}</a></li>
            </ul>
          </section>
        </div>

        <div className="legal-footer-links">
          <Link to="/">Return to Home</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/shipping">Shipping Policy</Link>
          <Link to="/returns">Returns Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
